/**
 * useAutoFocus — ref-setter that focuses a contenteditable element
 * when its block id matches the store's `focusBlockId`.
 *
 * Fires once per mount (via an internal ref guard), then clears
 * `focusBlockId` so the store returns to idle. Used by Notes
 * epic / story title nodes so newly-created blocks auto-focus
 * their heading for immediate typing.
 */
import { useMemo, useRef } from 'react'
import { useRequirementsViewStore } from '@/stores/requirementsViewStore'

export function useAutoFocus(blockId: string): (el: HTMLElement | null) => void {
  const focusBlockId = useRequirementsViewStore(s => s.focusBlockId)
  const setFocusBlockId = useRequirementsViewStore(s => s.setFocusBlockId)
  const done = useRef(false)
  return useMemo(() => (el: HTMLElement | null) => {
    if (!el || done.current) return
    if (focusBlockId === blockId) {
      done.current = true
      queueMicrotask(() => {
        el.focus()
        const range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
        setFocusBlockId(null)
      })
    }
  }, [blockId, focusBlockId, setFocusBlockId])
}

export function placeCaretAtEnd(el: HTMLElement): void {
  el.focus()
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(false)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}
